import { DateTime } from 'luxon'

// Partner API (api.johku.com) labels shared by the superadmin partner views (AdminPartners, AdminPartnerDetails,
// AdminPartnerIntegrationDetails, AdminPartnerApi) and the shop's own PartnerIntegrationsSettings.
// The labels are English POS translation keys: show them with _().

// The scopes in plain language, as the shop sees them when it gives consent (johku-api docs/contract.md §8.2)
export const PARTNER_SCOPES = [
  { scope: 'catalog:read', label: 'See your products and categories' },
  { scope: 'availability:read', label: 'Check availability and prices' },
  { scope: 'booking:write', label: 'Create bookings that your customers pay in your checkout' },
  { scope: 'booking:confirm', label: 'Create confirmed bookings without payment through Johku' },
  { scope: 'orders:read', label: 'Read your orders and reservations' },
  { scope: 'customers:read', label: 'Read your customer register' },
  { scope: 'reports:read', label: 'Read your sales reports' },
  // Granted to the integration by Johku, never by a shop: the backend leaves it out of shop connections
  { scope: 'atlas:read', label: 'Search all shops in Johku Atlas' }
]

export const PARTNER_STATUSES = [
  { status: 'pending', label: 'Pending', className: 'label-default' },
  { status: 'pilot', label: 'Pilot', className: 'label-info' },
  { status: 'verified', label: 'Verified', className: 'label-success' },
  { status: 'suspended', label: 'Suspended', className: 'label-danger' }
]

// Integrations are active or suspended; keys and shop connections have their own states
const OTHER_STATUSES = {
  active: { label: 'Active', className: 'label-success' },
  requested: { label: 'Waiting for approval', className: 'label-warning' },
  denied: { label: 'Denied', className: 'label-default' },
  revoked: { label: 'Revoked', className: 'label-default' },
  expired: { label: 'Expired', className: 'label-default' }
}

// The backend sends scopes as a list; a JSON or comma separated string is accepted too
export function normalizeScopes (scopes) {
  if (Array.isArray(scopes)) {
    return scopes.map(String)
  }
  if (typeof scopes === 'string' && scopes.trim()) {
    const text = scopes.trim()
    if (text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text)
        return Array.isArray(parsed) ? parsed.map(String) : []
      } catch (e) {
        return []
      }
    }
    return text.split(/[\s,]+/).filter(s => s)
  }
  return []
}

export default {
  data () {
    return {
      partnerScopes: PARTNER_SCOPES,
      partnerStatuses: PARTNER_STATUSES
    }
  },
  methods: {
    normalizeScopes,
    // The English key for a scope: show it with _(). An unknown scope is shown as it is.
    scopeLabelKey (scope) {
      const found = PARTNER_SCOPES.find(s => s.scope === scope)
      return found ? found.label : scope
    },
    scopeLabel (scope) {
      return this._(this.scopeLabelKey(scope))
    },
    statusInfo (status) {
      const partner = PARTNER_STATUSES.find(s => s.status === status)
      if (partner) {
        return partner
      }
      return OTHER_STATUSES[status] || { label: status || '', className: 'label-default' }
    },
    statusLabel (status) {
      const info = this.statusInfo(status)
      return info.label ? this._(info.label) : ''
    },
    statusClass (status) {
      return 'label ' + this.statusInfo(status).className
    },
    methodLabel (method) {
      if (method === 'self_connect') {
        return this._('Connected by a partner member')
      }
      if (method === 'request') {
        return this._('Approved request')
      }
      return method || ''
    },
    // Management API times are 'YYYY-MM-DD HH:MM:SS' in the shop timezone
    parseApiTime (value) {
      if (!value) {
        return null
      }
      const date = DateTime.fromSQL(String(value), { zone: this.timezone })
      return date.isValid ? date : null
    },
    formatApiTime (value) {
      const date = this.parseApiTime(value)
      if (!date) {
        return value || ''
      }
      return date.toFormat(this.dateFormat || 'd.M.yyyy') + ' ' + date.toFormat(this.timeFormat || 'H:mm')
    },
    formatApiDate (value) {
      const date = this.parseApiTime(value)
      if (!date) {
        return value || ''
      }
      return date.toFormat(this.dateFormat || 'd.M.yyyy')
    },
    formatCount (value) {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return ''
      }
      return number.toLocaleString(this.numberFormat || undefined)
    },
    // error_rate_24h is a share of the requests (0.05 = 5 %); a value over 1 is taken as a percentage
    formatRate (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      let number = Number(value)
      if (!Number.isFinite(number)) {
        return ''
      }
      if (number <= 1) {
        number *= 100
      }
      return number.toLocaleString(this.numberFormat || undefined, { maximumFractionDigits: 1 }) + ' %'
    },
    // A response list: {data: [...]}, or a plain array
    listData (data) {
      if (Array.isArray(data)) {
        return data
      }
      if (data && Array.isArray(data.data)) {
        return data.data
      }
      if (data && data.data && typeof data.data === 'object') {
        return Object.values(data.data)
      }
      return []
    }
  }
}
